<!-- @format -->
<template>
    <div class="activities-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3 v-if="contact.id">{{ contact.firstName }} Activities</h3>
                <h3 v-else>Activities</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-for="activity in activities" :key="activity.id" class="activity">
                    <div
                        class="image-container"
                        :style="{ background: `url('${activity.actor.imageUrl}')` }"
                    >
                        <img class="icon-image" :src="activity.details.iconUrl" alt="" />
                    </div>
                    <div class="type">{{ activity.details.type }}</div>
                    <div class="activity-content">
                        <div v-if="!contact.id" class="contact-info">
                            <span v-if="activity.contactId"
                                ><router-link
                                    :to="`/Contacts/${activity.contactId}`"
                                    @click.native="closeWidget"
                                    >{{ activity.contactName }}</router-link
                                ></span
                            >
                            <span v-if="activity.companyId">
                                from
                                <router-link
                                    :to="`/Companies/${activity.companyId}`"
                                    @click.native="closeWidget"
                                    >{{ activity.companyName | companyPlaceholder }}</router-link
                                ></span
                            >
                        </div>
                        <div class="details" v-html="activity.details.message"></div>
                        <div
                            v-if="activity.details.media && activity.details.media.length > 0"
                            class="media-container"
                        >
                            <contact-sms-media
                                v-for="{ key, contentType, url } in activity.details.media"
                                :key="key"
                                :content-type="contentType"
                                :url="url"
                            />
                        </div>
                        <div
                            v-show="activity.otherParticipants.length > 0"
                            class="participants-container"
                        >
                            <label>Other participants</label>
                            <div class="participants">
                                <activity-participant
                                    v-for="participant in activity.otherParticipants"
                                    :key="participant.key"
                                    :contact-id="participant.contactId"
                                    :user-id="participant.userId"
                                    :name="participant.name"
                                    :profile-image-url="participant.profileImageUrl"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="date">{{ activity.date }}</div>
                </div>

                <loading-screen-svg v-if="isLoading" />

                <div v-show="count !== activities.length" class="show-more-container">
                    <app-button
                        :loading="isLoading"
                        class="show-more-button"
                        @click="getActivities"
                        >{{ isLoading ? 'Loading...' : 'Show More Activites' }}</app-button
                    >
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button class="activity-btn" @click="openActivityWidget"
                        >Log Activity +</app-button
                    >
                    <app-button v-if="contact.id" @click="goToContact"
                        >See all activities for {{ contact.firstName }}</app-button
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const ActivityParticipant = () => import('@/components/activity/activity-participant');
const AppButton = () => import('@/components/common/app-button');
const ContactSmsMedia = () => import('@/components/contact/contact-sms/contact-sms-media');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'activities-widget',

    components: {
        'contact-sms-media': ContactSmsMedia,
        'app-button': AppButton,
        'activity-participant': ActivityParticipant,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
    },

    props: {
        options: {
            type: Object,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            clientId: null,
            type: null,
            startDate: null,
            endDate: null,
            contact: {
                id: null,
                firstName: null,
                lastName: null,
                name: null,
                profileImageUrl: null,
            },

            activities: [],
            page: 0,
            pageSize: 10,
            count: 0,
            isLoading: false,
        };
    },

    created() {
        this.clientId = this.widget.options.clientId;
        this.userId = this.widget.options.userId;
        this.type = this.widget.options.type;
        this.startDate = this.widget.options.startDate;
        this.endDate = this.widget.options.endDate;
        this.contact.id = this.widget.options.contactId;

        this.getActivities();
    },

    methods: {
        openActivityWidget() {
            EventBus.$emit('open-widget', {
                name: 'activity-widget',
                options: {
                    contactId: this.contact.id,
                    clientId: this.clientId,
                    type: {
                        id: null,
                        value: 'Call',
                    },
                },
            });
        },

        goToContact() {
            this.closeWidget();
            this.$router.push(`/Contacts/${this.contact.id}`);
        },

        getFilters() {
            var filters = [];

            if (this.userId) {
                filters.push(
                    `(TriDigital.EntityDomain.Entities.SalesActivity/UserId eq '${this.userId}' or ActivityParticipants/any(p: p/UserId eq '${this.userId}'))`,
                );
            }

            if (this.type === 'call') {
                filters.push("isof('TriDigital.EntityDomain.Entities.CallActivity')");
            } else if (this.type === 'email') {
                filters.push("isof('TriDigital.EntityDomain.Entities.EmailActivity')");
            } else if (this.type === 'meeting') {
                filters.push("isof('TriDigital.EntityDomain.Entities.MeetingActivity')");
            } else if (this.type === 'note') {
                filters.push("isof('TriDigital.EntityDomain.Entities.NoteActivity')");
            }

            if (this.startDate) {
                filters.push(`ActivityDate ge ${this.startDate.format('YYYY-MM-DDTHH:mm')}z`);
            }

            if (this.endDate) {
                filters.push(`ActivityDate le ${this.endDate.format('YYYY-MM-DDTHH:mm')}z`);
            }

            return filters;
        },

        getActivities() {
            this.isLoading = true;

            var topParam = `$top=${this.pageSize}`;
            var skipParam = `$skip=${this.page * this.pageSize}`;
            var orderByParam = '$orderby=ActivityDate desc';

            var expands = [
                'TriDigital.EntityDomain.Entities.SalesActivity/User($select=Id,FirstName,LastName,ProfileImageUrl)',
                'TriDigital.EntityDomain.Entities.ClickActivity/Campaign($select=Name)',
                'Message($select=Subject)',
                'ActivityParticipants($expand=User($select=Id,FirstName,LastName,ProfileImageUrl),Contact($select=Id,ProfileImageUrl,FirstName,LastName))',
                'TriDigital.EntityDomain.Entities.UnsubscribeActivity/UnsubscribeCampaign($select=Name)',
                'Contact($select=Id,ProfileImageUrl,FirstName,LastName;$expand=Company($select=Id,Name))',
                'TriDigital.EntityDomain.Entities.SmsActivity/Sms',
            ];

            var expandParam = `$expand=${expands.join(',')}`;

            var filterParam = '';
            var filters = this.getFilters();
            if (filters.length > 0) {
                filterParam = `$filter=${filters.join(' and ')}`;
            }

            var baseUrl = '/service/Activities?';
            if (this.contact.id) {
                baseUrl = `/service/Contacts(${this.contact.id})/Activities?`;
            } else if (this.clientId) {
                baseUrl = `/service/Activities?clientId=${this.clientId}`;
            }

            var query = `${baseUrl}&${topParam}&${skipParam}&${expandParam}&${filterParam}&${orderByParam}&$count=true`;

            this.$api
                .get(query)
                .then((response) => {
                    if (
                        this.contact.id &&
                        this.activities.length === 0 &&
                        response.data.value.length > 0
                    ) {
                        var firstActivity = response.data.value[0];
                        var contact =
                            firstActivity.Contact ||
                            firstActivity.ActivityParticipants.find(
                                (p) => p.ContactId === this.contact.id,
                            ).Contact;

                        this.contact.firstName = contact.FirstName;
                        this.contact.lastName = contact.LastName;
                        this.contact.name = this.$helpers.getName(
                            contact.FirstName,
                            contact.LastName,
                        );
                        this.contact.profileImageUrl =
                            contact.ProfileImageUrl || require('@/assets/images/avatar.png');
                    }

                    var newActivities = this.createActivities(response.data.value);
                    this.activities = this.activities.concat(newActivities);
                    this.page++;

                    this.count = response.data['@odata.count'];
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your activities',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        createActivities(activitiesResponse) {
            return activitiesResponse
                .map((activityResponse) => this.createActivity(activityResponse))
                .filter(
                    (activity) =>
                        activity.details.type !== 'Delivered' &&
                        activity.details.type !== 'Spam Report',
                );
        },

        createActivity(activityResponse) {
            var activity = {
                id: activityResponse.Id,
                date: this.$moment.utc(activityResponse.ActivityDate).local().format('MM/DD/YYYY'),
                actor: null,
                details: null,
                contactId: null,
                contactName: null,
                companyId: null,
                companyName: null,
                otherParticipants: [],
            };

            if (activityResponse.Contact) {
                activity.contactId = activityResponse.Contact.Id;
                activity.contactName = this.$helpers.getName(
                    activityResponse.Contact.FirstName,
                    activityResponse.Contact.LastName,
                );
                activity.companyId = activityResponse.Contact.Company
                    ? activityResponse.Contact.Company.Id
                    : null;
                activity.companyName = activityResponse.Contact.Company
                    ? activityResponse.Contact.Company.Name
                    : null;
            }

            activity.actor = this.getActor(activityResponse);
            activity.details = this.getDetails(activityResponse);
            activity.otherParticipants = this.getOtherParticipants(activity, activityResponse);

            return activity;
        },

        getOtherParticipants(activity, activityResponse) {
            if (
                activityResponse.ActivityParticipants &&
                activityResponse.ActivityParticipants.length > 0
            ) {
                return activityResponse.ActivityParticipants.filter(
                    (participant) =>
                        participant.ContactId !== activity.actor.id &&
                        participant.UserId !== activity.actor.id,
                ).map((participant) => {
                    if (participant.User) {
                        return {
                            key: participant.User.Id,
                            contactId: null,
                            userId: participant.User.Id,
                            name: this.$helpers.getName(
                                participant.User.FirstName,
                                participant.User.LastName,
                            ),
                            profileImageUrl:
                                participant.User.ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                        };
                    } else {
                        return {
                            key: participant.Contact.Id,
                            contactId: participant.Contact.Id,
                            userId: null,
                            name: this.$helpers.getName(
                                participant.Contact.FirstName,
                                participant.Contact.LastName,
                            ),
                            profileImageUrl:
                                participant.Contact.ProfileImageUrl ||
                                require('@/assets/images/avatar.png'),
                        };
                    }
                });
            } else {
                return [];
            }
        },

        getDetails(activityResponse) {
            var details = null;

            if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.ClickActivity'
            ) {
                details = this.getClickDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.DeliveredActivity'
            ) {
                details = this.getDeliveredDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.SpamReportActivity'
            ) {
                details = this.getSpamReportDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] === '#TriDigital.EntityDomain.Entities.OpenActivity'
            ) {
                details = this.getOpenDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] === '#TriDigital.EntityDomain.Entities.FormActivity'
            ) {
                details = this.getFormDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.UnsubscribeActivity'
            ) {
                details = this.getUnsubscribeDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.BlockActivity'
            ) {
                details = this.getBlockDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.BounceActivity'
            ) {
                details = this.getBounceDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] === '#TriDigital.EntityDomain.Entities.CallActivity'
            ) {
                details = this.getCallDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.EmailActivity'
            ) {
                details = this.getEmailDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] ===
                '#TriDigital.EntityDomain.Entities.MeetingActivity'
            ) {
                details = this.getMeetingDetails(activityResponse);
            } else if (
                activityResponse['@odata.type'] === '#TriDigital.EntityDomain.Entities.SmsActivity'
            ) {
                details = this.getSMSDetails(activityResponse);
            } else {
                // Note
                if (activityResponse.UserId) {
                    details = this.getNoteDetails(activityResponse);
                } else if (activityResponse.Message) {
                    details = this.getEmailOpenDetails(activityResponse);
                } else {
                    details = this.getBotDetails(activityResponse);
                }
            }

            if (details.message && details.message.includes('<!DOCTYPE')) {
                details.message = 'Cannot display graphical emails.';
            }

            var placedStyles =
                '<style>p{margin:0;padding:0;margin-bottom:10px;}p br{display:none}</style>';

            if (details.message && details.message.includes(placedStyles)) {
                details.message = details.message.replace(placedStyles, '');
            }

            return details;
        },

        getSMSDetails(activityResponse) {
            const parsedMedia = JSON.parse(activityResponse.Sms.MediaUris);

            const asMedia = (mediaUri) => {
                return {
                    key: this.$helpers.createId('media'),
                    url: mediaUri.Uri,
                    contentType: mediaUri.ContentType,
                };
            };

            return {
                iconUrl: require('@/assets/images/SVG/Activities/sms.svg'),
                color: 'red-accent',
                type: 'SMS',
                message: activityResponse.Sms.Body,
                media: parsedMedia ? parsedMedia.map(asMedia) : [],
            };
        },

        getClickDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/clicked.svg'),
                color: 'red-accent',
                type: null,
                message: null,
            };

            var description = activityResponse.IsViewInBrowserClick
                ? 'Viewed in browser'
                : `Clicked on: <a target="_blank" href="${activityResponse.ClickUrl}">${activityResponse.ClickUrl}</a>`;

            if (activityResponse.Campaign) {
                details.type = 'Campaign Click-Through';
                details.message = `<strong>${activityResponse.Campaign.Name}</strong><br/>${description}`;
            } else if (activityResponse.Message) {
                details.type = 'Email Click';
                details.message = `<strong>Subject</strong><br/>${activityResponse.Message.Subject || 'No content'}<br/>${description}`;
            } else {
                details.type = 'Campaign Click-Through';
                details.message = 'No content';
            }

            return details;
        },

        getOpenDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/email-open.svg'),
                color: 'blue-accent',
                type: null,
                message: null,
            };

            if (activityResponse.Campaign) {
                details.type = 'Campaign Open';
                details.message = `<strong>${activityResponse.Campaign.Name}</strong></br>`;
            } else if (activityResponse.Message) {
                details.type = 'Email Open';
                details.message = `<strong>Subject</strong><br/>${activityResponse.Message.Subject || 'No content'}`; //<br/>${ activityResponse.Message.HtmlBody}
            }

            return details;
        },

        getDeliveredDetails(activityResponse) {
            var details = {
                iconUrl: null,
                color: 'blue-accent',
                type: 'Delivered',
                message: '',
            };

            return details;
        },

        getSpamReportDetails(activityResponse) {
            var details = {
                iconUrl: null,
                color: 'red-accent',
                type: 'Spam Report',
                message: '',
            };

            return details;
        },

        getFormDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/form-submission.svg'),
                color: 'orange-accent',
                type: 'Form Submission',
                message: `<strong>${activityResponse.FormType}</strong><br/>${activityResponse.FormComment || ''}`,
            };

            return details;
        },

        getUnsubscribeDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/hand.svg'),
                color: 'red-accent',
                type: 'Unsubscribed From',
                message: activityResponse.UnsubscribeCampaign.Name,
            };

            return details;
        },

        getBounceDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/hand.svg'),
                color: 'red-accent',
                type: 'Bounced',
                message: activityResponse.BounceReason,
            };

            return details;
        },

        getBlockDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/hand.svg'),
                color: 'red-accent',
                type: 'Blocked',
                message: activityResponse.BounceReason,
            };

            return details;
        },

        getCallDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/call.svg'),
                color: 'red-accent',
                type: 'Follow Up Call',
                message: activityResponse.SalesNote,
            };

            return details;
        },

        getEmailDetails(activityResponse) {
            var details = {
                iconUrl: null,
                color: null,
                type: null,
                message: activityResponse.SalesNote,
            };

            if (activityResponse.UserId) {
                details.type = 'Follow Up Email';
                details.iconUrl = require('@/assets/images/SVG/Activities/sent.svg');
                details.color = 'blue-accent';
            } else {
                details.type = 'Email Reply';
                details.iconUrl = require('@/assets/images/SVG/Activities/received.svg');
                details.color = 'orange-accent';
            }

            if (activityResponse.Message != null) {
                details.message = `<strong>Subject</strong><br/>${activityResponse.Message.Subject}`;
            }

            return details;
        },

        getMeetingDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/meeting.svg'),
                color: 'blue-accent',
                type: 'Meeting',
                message: activityResponse.MeetingSubject || 'No subject',
            };

            return details;
        },

        getNoteDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/chat.svg'),
                color: 'purple-accent',
                type: 'Note',
                message: activityResponse.SalesNote,
            };

            return details;
        },

        getEmailOpenDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/SVG/Activities/sent.svg'),
                color: 'green-accent',
                type: 'Email Open',
                message: activityResponse.Message.Subject || 'No content',
            };

            return details;
        },

        getBotDetails(activityResponse) {
            var details = {
                iconUrl: require('@/assets/images/glasshive-icon-stroke.png'),
                color: 'purple-accent',
                type: 'HoneyBot Note',
                message: activityResponse.SalesNote,
            };

            return details;
        },

        getActor(activityResponse) {
            var actor = {
                type: null,
                id: null,
                name: null,
                imageUrl: null,
            };

            var firstUserParticipant = null;
            var firstContactParticipant = null;

            if (
                activityResponse.ActivityParticipants &&
                activityResponse.ActivityParticipants.length > 0
            ) {
                firstUserParticipant = activityResponse.ActivityParticipants.find((p) => p.User);
                firstContactParticipant = activityResponse.ActivityParticipants.find(
                    (p) => p.Contact,
                );
            }

            var user =
                activityResponse.User || (firstUserParticipant ? firstUserParticipant.User : null);
            var contact =
                activityResponse.Contact ||
                (firstContactParticipant ? firstContactParticipant.Contact : null);

            if (user) {
                actor.type = 'user';
                actor.id = user.Id;
                actor.imageUrl = user.ProfileImageUrl || require('@/assets/images/avatar.png');
                actor.name = this.$helpers.getName(user.FirstName, user.LastName);
            } else if (contact) {
                actor.type = 'contact';
                actor.id = contact.Id;
                actor.imageUrl = contact.ProfileImageUrl;
                actor.name = this.$helpers.getName(contact.FirstName, contact.LastName);
            }

            return actor;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.activities-widget.widget .content-wrapper {
    padding: 0;
}

.activities-widget .loading-screen {
    text-align: center;
}

.activities-widget .show-more-container {
    padding: 20px;
}

.activities-widget .show-more-container .show-more-button {
    width: 100%;
}

.activities-widget .activity {
    padding: 15px;
    border-bottom: 1px solid #e9e9ef;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
}

.activities-widget .activity .image-container {
    position: relative;
    overflow: visible;
    height: 50px;
    width: 50px;
    background-repeat: no-repeat !important;
    background-color: #fff !important;
    background-size: 100% !important;
    background-position: center !important;
    border-radius: 50%;
    display: inline-block;
    *display: inline;
    zoom: 1;
    flex-shrink: 0;
}

.activities-widget .activity .image-container .icon-image {
    position: absolute;
    bottom: 0;
    right: 0;
    max-width: 20px;
}

.activities-widget .activity .type {
    width: 80px;
    font-size: 12px;
    flex-shrink: 0;
}

.activities-widget .activity .activity-content {
    width: 300px;
    font-size: 12px;
}

.activities-widget .activity .activity-content .media-container {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
}

.activities-widget .activity .activity-content .media-container .contact-sms-media img,
.activities-widget .activity .activity-content .media-container .contact-sms-media video {
    max-height: 100px;
}

.activities-widget .activity .date {
    font-size: 14px;
    flex-shrink: 0;
}

.activities-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.activities-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.activities-widget .action-container {
    display: flex;
}

.activities-widget .action-container .activity-btn {
    color: #18aeff;
    background: transparent;
    border: none;
    margin-right: 10px;
}

.activities-widget .action-container .activity-btn:hover {
    background: #dbedfa;
}

.activities-widget .contact-info a {
    color: #18aeff;
}

.activities-widget .participants-container {
    margin-top: 20px;
}

.activities-widget .participants-container .participants {
    display: flex;
    flex-wrap: wrap;
}

.activities-widget .participants-container .activity-participant {
    margin-right: 5px;
    margin-bottom: 5px;
}

.read-only .activities-widget .activity-btn {
    display: none;
}

.activities-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
